import { io, Socket } from 'socket.io-client';
import type { GroupMessage, User } from '../types';

// Callbacks the React layer registers so it can mirror the socket's
// connection lifecycle into component state and trigger a refetch/rejoin
// after an automatic reconnect.
interface ConnectionListeners {
  onConnect?: () => void;
  onDisconnect?: (reason: string) => void;
  onReconnect?: () => void;
}

class SocketService {
  private socket: Socket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  // Provider for the current auth token. Stored as a getter (not a captured
  // string) so socket.io can pull a *fresh* token on every (re)connection
  // attempt via the `auth` callback below.
  private getToken: (() => string) | null = null;
  private connectionListeners: ConnectionListeners = {};

  /**
   * Register lifecycle callbacks. These persist across automatic reconnects
   * because the underlying socket instance is reused while reconnection is on.
   */
  setConnectionListeners(listeners: ConnectionListeners): void {
    this.connectionListeners = listeners;
  }

  connect(tokenProvider: string | (() => string)): Promise<void> {
    return new Promise((resolve, reject) => {
      // Keep a live token getter so automatic reconnects re-read the latest
      // token (it may have been refreshed while we were offline).
      this.getToken = typeof tokenProvider === 'function'
        ? tokenProvider
        : () => tokenProvider;

      // Clean up existing connection first
      if (this.socket?.connected) {
        resolve();
        return;
      }

      // Disconnect existing socket if it exists but isn't connected
      if (this.socket) {
        this.socket.disconnect();
        this.socket = null;
      }

      const socketUrl = (import.meta as any).env?.VITE_SOCKET_URL || 'https://dev.solofix.nl';

      // Reset reconnect attempts for new connection
      this.reconnectAttempts = 0;

      this.socket = io(socketUrl, {
        // socket.io re-invokes this callback before every (re)connection
        // attempt, so a token refreshed during an outage is picked up
        // automatically on the next handshake.
        auth: (cb: (data: object) => void) => {
          cb({ token: this.getToken?.() || '' });
        },
        // Send the httpOnly auth cookie on the handshake so the socket can
        // authenticate after a reload when no token is held in memory.
        withCredentials: true,
        transports: ['websocket', 'polling'],
        timeout: 15000, // Increased timeout
        // Let socket.io handle reconnection automatically. Combined with the
        // server-side connectionStateRecovery (2 min) this restores the
        // session transparently after a brief network dip.
        reconnection: true,
        reconnectionAttempts: Infinity,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 10000,
        // NOTE: `forceNew` intentionally omitted so connectionStateRecovery
        // can rehydrate the previous session on reconnect.
      });

      // Bridge the Manager-level "reconnect" event (fires after a successful
      // automatic reconnection) to the React layer so it can rejoin rooms and
      // refetch anything missed while offline.
      this.socket.io.on('reconnect', () => {
        this.connectionListeners.onReconnect?.();
      });

      // Set up connection timeout
      const connectionTimeout = setTimeout(() => {
        if (this.socket && !this.socket.connected) {
          this.socket.disconnect();
          reject(new Error('Connection timeout'));
        }
      }, 20000);

      this.socket.on('connect', () => {
        clearTimeout(connectionTimeout);
        this.reconnectAttempts = 0;
        // Fires on the initial connect AND on every automatic reconnect;
        // notify the React layer so `isConnected` is always accurate.
        this.connectionListeners.onConnect?.();
        resolve();
      });

      this.socket.on('connect_error', (error: any) => {
        
        clearTimeout(connectionTimeout);
        
        // Handle authentication errors - don't retry, let parent handle
        if (error.message?.includes('expired') || 
            error.message?.includes('invalid') || 
            error.message?.includes('Authentication') ||
            error.message?.includes('token')) {
          reject(error);
          return;
        }
        
        this.reconnectAttempts++;
        
        if (this.reconnectAttempts >= this.maxReconnectAttempts) {
          reject(new Error('Kon niet verbinden met chat server na meerdere pogingen'));
        } else {
          // Don't reject yet, let socket.io handle the retry
        }
      });

      this.socket.on('disconnect', (reason: any) => {
        clearTimeout(connectionTimeout);
        // Always propagate the disconnect so the UI stops showing "Verbonden".
        // socket.io will auto-reconnect for network-level drops (transport
        // close / ping timeout). For an explicit "io server disconnect" it
        // will NOT auto-reconnect (socket.active === false), and the React
        // layer's auto-connect effect takes over instead.
        this.connectionListeners.onDisconnect?.(reason);
      });

      this.socket.on('error', (_error: any) => {
        clearTimeout(connectionTimeout);
      });
    });
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
  }

  isConnected(): boolean {
    return this.socket?.connected || false;
  }

  /**
   * True when a socket exists and will (re)connect on its own — i.e. it is
   * connected OR currently in an automatic reconnection cycle. The React
   * auto-connect effect uses this to avoid spawning a duplicate socket while
   * socket.io is already retrying an existing one.
   */
  isActive(): boolean {
    return this.socket?.active || false;
  }

  // Group management
  joinGroup(groupId: number): void {
    if (!this.socket?.connected) {
      throw new Error('Geen verbinding met chat server');
    }
    this.socket.emit('join-group', { groupId: groupId.toString() });
  }

  leaveGroup(groupId: number): void {
    if (!this.socket?.connected) {
      throw new Error('Geen verbinding met chat server');
    }
    this.socket.emit('leave-group', { groupId: groupId.toString() });
  }

  // Fetch the current online users of a group (initial snapshot; later
  // changes arrive via the 'online-users' broadcast)
  getOnlineUsers(groupId: number): Promise<any[]> {
    return new Promise((resolve) => {
      if (!this.socket?.connected) {
        resolve([]);
        return;
      }
      this.socket.emit('get-online-users', { groupId: groupId.toString() }, (response: any) => {
        resolve(response?.success && Array.isArray(response.data) ? response.data : []);
      });
    });
  }

  // Mark group chat as read (fire-and-forget)
  markGroupRead(groupId: number): void {
    if (!this.socket?.connected) return;
    this.socket.emit('mark-group-read', { groupId: groupId.toString() });
  }

  // Message management
  sendMessage(groupId: number, content: string): void {
    if (!this.socket?.connected) {
      throw new Error('Geen verbinding met chat server');
    }
    this.socket.emit('send-message', { groupId, content });
  }

  // Typing indicators
  startTyping(groupId: number): void {
    if (!this.socket?.connected) return;
    this.socket.emit('typing-start', { groupId: groupId.toString(), isTyping: true });
  }

  stopTyping(groupId: number): void {
    if (!this.socket?.connected) return;
    this.socket.emit('typing-stop', { groupId: groupId.toString(), isTyping: false });
  }

  // Event listeners
  onNewMessage(callback: (message: GroupMessage) => void): void {
    this.socket?.on('new-message', callback);
  }

  onUserJoined(callback: (data: { groupId: number; user: User }) => void): void {
    this.socket?.on('user-joined', callback);
  }

  onUserLeft(callback: (data: { groupId: number; user: User }) => void): void {
    this.socket?.on('user-left', callback);
  }

  onMemberAccepted(callback: (data: { groupId: number; user: User }) => void): void {
    this.socket?.on('member-accepted', callback);
  }

  onMemberRejected(callback: (data: { groupId: number; userId: number }) => void): void {
    this.socket?.on('member-rejected', callback);
  }

  onUserTyping(callback: (data: { groupId: number; user: User }) => void): void {
    this.socket?.on('user-typing', callback);
  }

  onUserStoppedTyping(callback: (data: { groupId: number; user: User }) => void): void {
    this.socket?.on('user-stopped-typing', callback);
  }

  onError(callback: (error: { message: string; code?: string }) => void): void {
    this.socket?.on('error', callback);
  }

  onOnlineUsers(callback: (data: { groupId: string; users: any[] }) => void): void {
    this.socket?.on('online-users', callback);
  }

  offOnlineUsers(callback?: (data: { groupId: string; users: any[] }) => void): void {
    this.socket?.off('online-users', callback);
  }

  // Notification event listeners
  onNewNotification(callback: (notification: any) => void): void {
    this.socket?.on('notification:new', callback);
  }

  onNotificationCountUpdate(callback: (count: number) => void): void {
    this.socket?.on('notification:count', callback);
  }

  offNewNotification(callback?: (notification: any) => void): void {
    if (callback) {
      this.socket?.off('notification:new', callback);
    } else {
      this.socket?.removeAllListeners('notification:new');
    }
  }

  offNotificationCountUpdate(callback?: (count: number) => void): void {
    if (callback) {
      this.socket?.off('notification:count', callback);
    } else {
      this.socket?.removeAllListeners('notification:count');
    }
  }

  // Remove listeners
  offNewMessage(callback?: (message: GroupMessage) => void): void {
    this.socket?.off('new-message', callback);
  }

  offUserJoined(callback?: (data: { groupId: number; user: User }) => void): void {
    this.socket?.off('user-joined', callback);
  }

  offUserLeft(callback?: (data: { groupId: number; user: User }) => void): void {
    this.socket?.off('user-left', callback);
  }

  offMemberAccepted(callback?: (data: { groupId: number; user: User }) => void): void {
    this.socket?.off('member-accepted', callback);
  }

  offMemberRejected(callback?: (data: { groupId: number; userId: number }) => void): void {
    this.socket?.off('member-rejected', callback);
  }

  offUserTyping(callback?: (data: { groupId: number; user: User }) => void): void {
    this.socket?.off('user-typing', callback);
  }

  offUserStoppedTyping(callback?: (data: { groupId: number; user: User }) => void): void {
    this.socket?.off('user-stopped-typing', callback);
  }

  offError(callback?: (error: { message: string; code?: string }) => void): void {
    this.socket?.off('error', callback);
  }

  // Direct messaging
  sendDirectMessage(receiverId: number, message: string, messageType: 'TEXT' | 'EMOJI' | 'GIF' | 'IMAGE' = 'TEXT', gifUrl?: string, gifId?: string): Promise<any> {
    return new Promise((resolve, reject) => {
      if (!this.socket?.connected) {
        reject(new Error('Geen verbinding met chat server'));
        return;
      }

      this.socket.emit('direct-message:send', {
        receiverId,
        message,
        messageType,
        gifUrl,
        gifId
      }, (response: any) => {
        if (response?.success) {
          resolve(response.data);
        } else {
          reject(new Error(response?.error || 'Failed to send message'));
        }
      });
    });
  }

  markConversationAsRead(userId: number): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.socket?.connected) {
        reject(new Error('Geen verbinding met chat server'));
        return;
      }

      this.socket.emit('conversation:mark-read', { userId }, (response: any) => {
        if (response?.success) {
          resolve();
        } else {
          reject(new Error(response?.error || 'Failed to mark conversation as read'));
        }
      });
    });
  }

  // Direct message event listeners
  onDirectMessageReceived(callback: (message: any) => void): void {
    this.socket?.on('direct-message:new', callback);
  }

  onConversationRead(callback: (data: { userId: number }) => void): void {
    this.socket?.on('conversation:read', callback);
  }

  // Remove direct message listeners
  offDirectMessageReceived(callback?: (message: any) => void): void {
    if (callback) {
      this.socket?.off('direct-message:new', callback);
    } else {
      this.socket?.removeAllListeners('direct-message:new');
    }
  }

  offConversationRead(callback?: (data: { userId: number }) => void): void {
    if (callback) {
      this.socket?.off('conversation:read', callback);
    } else {
      this.socket?.removeAllListeners('conversation:read');
    }
  }

  // Remove all listeners
  removeAllListeners(): void {
    this.socket?.removeAllListeners();
  }
}

export const socketService = new SocketService();